import type { Article } from "./types";

export const article: Article = {
  slug: "morning-quiet-time-routine",
  title: "A quiet time routine that survives a phone on the nightstand",
  h1: "A quiet time that survives your phone",
  description:
    "Most quiet times fail in the first ninety seconds of the day, before any decision is made. Here is a routine built around that, not against it.",
  targets: [
    "quiet time routine",
    "how to have a quiet time with God",
    "morning devotional routine",
    "cant focus during quiet time",
  ],
  published: "2026-08-17",
  standfirst:
    "The problem is almost never the plan. It is that the plan starts ninety seconds after you have already opened something else.",
  takeaways: [
    "A quiet time is lost in the first ninety seconds of the day, before any decision is made — so the intervention has to sit before the phone, not inside the routine.",
    "Anchor it to something that already happens every morning (the kettle, the shower, the first coffee) rather than to a time, which requires you to remember.",
    "Ten minutes, three parts: a short passage, one honest sentence to God, one person prayed for by name. Long routines are the ones that get skipped when the day is compressed.",
    "Expect to be bored and distracted. Both are normal and neither means it is not working; the practice is returning, not concentrating.",
  ],
  body: [
    {
      type: "p",
      text: "Almost everyone who wants a daily quiet time has already tried one. The plan is rarely the problem — the plans are fine, and there are thousands of them. What actually happens is this: you wake, the phone is the nearest object, you check one thing, and by the time you are upright the morning has a different shape and the quiet time has become a thing you will do later, which means tomorrow.",
    },
    {
      type: "p",
      text: "So a routine that works has to be built around the first ninety seconds of the day, because that is where it is lost. Everything below is arranged around that fact.",
    },
    {
      type: "h2",
      id: "before",
      text: "The intervention goes before the phone, not inside the routine",
    },
    {
      type: "p",
      text: "If your quiet time competes with your phone, it loses — not because it is less important but because it is slower to start and it asks more of you at the precise moment you have least to give. The fix is not more resolve at 6:40am. It is that the phone should not be available yet.",
    },
    {
      type: "ul",
      items: [
        "Charge it across the room, or in another room. This one change does more than every plan on this page combined, and it is also the one people resist hardest.",
        "Use an actual alarm clock. Nine pounds, and it removes the single strongest argument for having the phone at arm's reach.",
        "If neither is possible, block the feed apps until an hour after you wake. Not the phone — the two apps that take the ninety seconds.",
      ],
    },
    {
      type: "note",
      text: "You are not trying to have more willpower in the morning. You are trying to need less of it.",
    },
    {
      type: "h2",
      id: "anchor",
      text: "Anchor it to an event, never to a time",
    },
    {
      type: "p",
      text: '"6:30am" requires you to remember, decide and act, all before you are properly awake. "While the kettle boils" requires none of those — the kettle is already happening, every day, and the routine rides on it.',
    },
    {
      type: "p",
      text: "Pick something that already occurs every single morning without your involvement: the kettle, the coffee machine, the first ten minutes after the shower, the bus. The anchor is the whole trick. A quiet time attached to an existing habit survives holidays, bad nights and travel; one attached to a clock survives about eleven days.",
    },
    {
      type: "h2",
      id: "shape",
      text: "Ten minutes, three parts",
    },
    {
      type: "p",
      text: "Long routines are the ones that get dropped, because most mornings are compressed and a forty-minute practice cannot be shortened gracefully — it can only be skipped. Ten minutes with three fixed parts can be done badly on a bad day, which is the property that keeps it alive.",
    },
    {
      type: "ol",
      items: [
        "A short passage, read slowly. Not a chapter. Ten verses, or five, or one — read twice. The aim is not coverage, and a plan that leaves you feeling behind is a plan you will eventually resent.",
        'One honest sentence to God. Whatever is actually true this morning, including "I do not want to be here". Honesty is the only entry requirement, and a prayer you do not mean is worth less than a short one you do.',
        "One person, by name. It moves the practice out of your own head, which is where a solitary routine otherwise stays, and it is the part people report missing when they stop.",
      ],
    },
    {
      type: "p",
      text: "That is the whole routine. Everything else — journals, plans, commentaries, longer prayer — is an addition to make once this one is boringly automatic.",
    },
    {
      type: "h2",
      id: "distraction",
      text: "On being bored and distracted",
    },
    {
      type: "p",
      text: "You will be. Ten minutes into a passage you will find you have read four verses without taking in any of them, and your mind will have gone to an email. This is not evidence that it is not working, and it is not a sign of a spiritual problem. Attention wanders; the practice is the returning, not the concentrating.",
    },
    {
      type: "p",
      text: "The one thing that genuinely damages it is judging each session. A quiet time evaluated every morning becomes a performance you can fail, and a performance you can fail is a performance you will eventually stop attending.",
    },
    {
      type: "verse",
      verse: {
        text: "Yahweh, in the morning you will hear my voice. In the morning I will lay my requests before you, and will watch expectantly.",
        ref: "Psalm 5:3",
      },
    },
    {
      type: "h2",
      id: "restart",
      text: "When you miss a week",
    },
    {
      type: "p",
      text: "You will miss a week. Illness, travel, a hard stretch at work, a season where mornings belong to a small child. The routine does not survive on consistency; it survives on how you restart.",
    },
    {
      type: "p",
      text: 'Restart the same day you notice, at half the size. Not "back to ten minutes tomorrow" — three minutes, this morning, one passage, one sentence. The instinct to resume at full strength tomorrow is the instinct that turns one missed week into three months.',
    },
    {
      type: "p",
      text: "{Bible-Shield} was built around the same observation about evenings that this piece makes about mornings: the moment is lost before any decision happens, so the help has to arrive there. A verse finds you on the home screen whether or not you open anything, and the apps that take the first ninety seconds can be closed until you have had them. If evenings are your harder end of the day, {the-evening-version-of-this-problem} is its own piece.",
      links: {
        "Bible-Shield": "/",
        "the-evening-version-of-this-problem":
          "/blog/how-to-stop-scrolling-at-night",
      },
    },
    {
      type: "p",
      text: "None of this assumes the shape of your morning is the one described here. It is one pattern among several, and the reason most quiet times fail is that the method was somebody else's rather than that the person was undisciplined — {the-prayer-style-test} names five of them and the way each one dries up.",
      links: { "the-prayer-style-test": "/quiz/prayer-style-quiz" },
    },
  ],
  faq: [
    {
      q: "What time should a quiet time be?",
      a: "Whenever you are reliably awake and alone — which for many people is not early. The hour matters far less than attaching it to something that already happens every day, because that is what removes the need to remember.",
    },
    {
      q: "How long should it be?",
      a: "Ten minutes, until ten minutes is automatic. A routine that cannot be done badly on a bad morning gets skipped rather than shortened, and skipping is what ends it.",
    },
    {
      q: "Is it wrong to use a phone for it?",
      a: "No — a Bible on a phone is still a Bible. The risk is only that the device carrying the passage also carries everything else, which is an argument for blocking the rest during that window rather than for printing your Bible.",
    },
    {
      q: "What if I feel nothing?",
      a: "That is the usual experience and it is not a verdict on the practice. Most of a long habit is unremarkable; the sessions that feel like something are the exception rather than the standard being missed.",
    },
  ],
  related: ["how-to-stop-scrolling-at-night", "bible-in-a-year-on-your-phone"],
};
